import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useAqtQuery } from '@/hooks/useAqtQuery';

import BrandAvatar from './BrandAvatar';

const CONTAINER_SIZE = 45;
const GROUP_AVATAR_SIZE = 30;

const Single = styled.div`
  width: ${CONTAINER_SIZE}px;
  height: ${CONTAINER_SIZE}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Group = styled.div<{ $hasOnClick: boolean }>`
  position: relative;
  width: ${CONTAINER_SIZE}px;
  height: ${CONTAINER_SIZE}px;
  flex-shrink: 0;
  cursor: ${props => (props.$hasOnClick ? 'pointer' : 'default')};

  > :first-child {
    position: absolute;
    left: 0;
    bottom: 0;
  }

  > :last-child {
    position: absolute;
    top: 0;
    right: 0;
    box-shadow: 0 0 0 2px ${props => props.theme.brand.bgBase};
    border-radius: 50%;
  }
`;

type Props = {
  cognitoIds?: Array<string>;
  handleGroupAvatar?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  PlaceholderImage: React.ReactNode;
};

/** Avatar(s) for a notification's actor(s). Resolves the real users by cognitoId so
 *  uploaded photos show and no-upload users fall back to brand-gradient initials via
 *  the shared `BrandAvatar` — never the `default_avatar` silhouette. */
const CustomAvatar: React.FC<Props> = ({ cognitoIds, handleGroupAvatar, PlaceholderImage }) => {
  const navigate = useNavigate();
  const ids = cognitoIds ?? [];
  const idsKey = ids.join(',');

  const { data: users } = useAqtQuery(['userByCognitoId', { idsKey }], [idsKey], {
    enabled: ids.length > 0,
  });

  // Until the users resolve (or when there's no actor) show the category placeholder
  // glyph rather than a blank initials circle.
  if (ids.length === 0 || !users) {
    return <Single>{PlaceholderImage}</Single>;
  }

  const userFor = (id: string) => users.find(user => user.cognitoId === id);

  if (ids.length === 1) {
    const user = userFor(ids[0]);
    return (
      <Single>
        <BrandAvatar
          src={user?.avatarLink}
          name={user?.friendlyName}
          size={CONTAINER_SIZE}
          onClick={() => navigate(`/profile/${ids[0]}/posts`)}
        />
      </Single>
    );
  }

  const first = userFor(ids[0]);
  const second = userFor(ids[1]);
  return (
    <Group $hasOnClick={!!handleGroupAvatar} onClick={handleGroupAvatar}>
      <BrandAvatar src={first?.avatarLink} name={first?.friendlyName} size={GROUP_AVATAR_SIZE} />
      <BrandAvatar src={second?.avatarLink} name={second?.friendlyName} size={GROUP_AVATAR_SIZE} />
    </Group>
  );
};

export default CustomAvatar;
